/* eslint-disable no-alert */
import React, { useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardTitle,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import ZebraFormModal from '../../components/Zebra/ZebraFormModal';
import { post } from '../../helpers/api_helper';

function Printers(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
    timeOut: 8000,
  };

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalPrinter, setEditModalPrinter] = useState({});
  const [signal, setSignal] = useState(0);
  const [printerStatuses, setPrinterStatuses] = useState({});

  const setPrinterStatus = (printer, status) => {
    setPrinterStatuses((current) => ({
      ...current,
      [printer.id]: status,
    }));
  };

  const getPrinterActionClass = (printer) => {
    const status = printerStatuses[printer.id];
    const isWorking = status ? status === 'success' : printer.last_success;

    return `btn btn-sm ${isWorking ? 'btn-soft-success' : 'btn-soft-danger'}`;
  };

  const editPrinter = (printer) => {
    setEditModalPrinter(printer);
    setEditModalOpen(true);
  };

  const onEditSuccess = () => {
    setSignal(signal + 1);
    setEditModalOpen(false);
    setEditModalPrinter(null);
  };

  const deletePrinter = async (printer, e) => {
    const btn = e.currentTarget;

    btn.disabled = true;
    btn.blur();
    btn.firstChild.classList.toggle('bx-spin');

    // eslint-disable-next-line no-restricted-globals
    const cnfrm = confirm(props.t(`Are you sure you want to delete ${printer.name}?`));

    if (cnfrm) {
      try {
        await post(`/api/zebra/printer/${printer.id}/delete`);

        toastr.success(`Deleted printer: ${printer.name}`, 'Success');
        setSignal(signal + 1);
      } catch (error) {
        toastr.error(error.message, 'Error');
      } finally {
        btn.disabled = false;
        btn.firstChild.classList.toggle('bx-spin');
      }
    }
  };

  const testPrinter = async (printer, e) => {
    const btn = e.currentTarget;
    const icon = btn.firstChild;

    btn.disabled = true;
    btn.blur();
    icon.classList.add('bx-spin');

    try {
      const result = await post(`/api/zebra/printer/${printer.id}/test`);
      toastr.success(`Printer says it printed: ${printer.name} (${result.response.split(',')[0]})`, 'Success');
      setPrinterStatus(printer, 'success');
    } catch (error) {
      toastr.error(`Error on test: ${error.message}`, 'Error');
      setPrinterStatus(printer, 'failure');
    } finally {
      btn.disabled = false;
      icon.classList.remove('bx-spin');
    }
  };

  const pingPrinter = async (printer, e) => {
    const btn = e.currentTarget;
    const icon = btn.firstChild;

    btn.disabled = true;
    btn.blur();
    icon.classList.add('bx-spin');

    try {
      const result = await post(`/api/zebra/printer/${printer.id}/ping`);
      toastr.success(`Printer is reachable: ${printer.name} (${result.response.split(',')[0]})`, 'Success');
      setPrinterStatus(printer, 'success');
    } catch (error) {
      toastr.error(`Printer is not reachable: ${error.message}`, 'Error');
      setPrinterStatus(printer, 'failure');
    } finally {
      btn.disabled = false;
      icon.classList.remove('bx-spin');
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="8">
            <Card>
              <CardBody>
                <CardTitle className="h4">{props.t('Zebra Printers')}</CardTitle>

                <Can i="see" this="printers">
                  <BaseGrid
                    url="/api/zebra"
                    shouldUpdate={signal}
                    create={() => { setEditModalOpen(true); }}
                    config={
                    {
                      columns: {
                        id: { label: 'ID', sort: true },
                        name: { label: 'Name', sort: true },
                        ip: { label: 'IP', sort: false },
                        port: { label: 'Port', sort: true },
                        notes: { label: 'Notes', sort: false },
                        actions: {
                          label: props.t('Actions'),
                          // Format this
                          actions: [
                            {
                              key: 'ping',
                              icon: 'mdi mdi-radio-tower',
                              classFor: (item) => getPrinterActionClass(item),
                              alt: props.t('Ping Printer IP'),
                              click: pingPrinter,
                            },
                            {
                              key: 'test-print',
                              icon: 'mdi mdi-test-tube',
                              classFor: (item) => getPrinterActionClass(item),
                              alt: props.t('Test Print'),
                              click: testPrinter,
                            },
                            {
                              key: 'edit',
                              icon: 'bx bx-pencil',
                              class: 'btn btn-sm btn-soft-dark',
                              alt: props.t('Edit'),
                              click: editPrinter,
                            },
                            {
                              key: 'delete',
                              icon: 'mdi mdi-delete',
                              class: 'btn btn-sm btn-soft-danger',
                              alt: props.t('Delete'),
                              click: deletePrinter,
                            },
                          ],
                        },
                      },
                      pagination: {
                        sort: 'id',
                        direction: 'desc',
                        perPage: 30,
                      },
                    }
                    }
                  />

                  <ZebraFormModal
                    shown={editModalOpen}
                    printer={editModalPrinter}
                    onSuccess={onEditSuccess}
                    onClose={() => { setEditModalOpen(false); setEditModalPrinter(null); }}
                  />
                </Can>

                <Can not i="see" this="printers"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Printers.defaultProps = {
  t: (x) => (x),
};

Printers.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Printers));
